import { bffInstance } from "../api/axiosInstance";
import { UserSettings, UserLocale ,UserAppView} from "./Interface";
import {
  useQuery,
  useQueryClient,
  useMutation,
} from "react-query";
import useUnifyUserProfileStore from "../core/UserProfileStore";
import { useGlobalStore } from '../core/GlobalStore';
import { useNotificationStore } from "../notification/NotificationStore";

const updateTheme = async (updatedSettings: UserSettings) => {
  const res = await bffInstance.put<UserSettings>(
    "/updateUserPreferences",
    updatedSettings
  );
  return res.data;
};

const fetchSettings = async () => {
  const res = await bffInstance.get<UserSettings>("/getUserPreferences");
  return res.data;
};

export const useUpdateTheme = () => {
  const queryClient = useQueryClient();
  const { setTheme } = useUnifyUserProfileStore();

  const { data, mutate } = useMutation(
    (settings: UserSettings) => updateTheme(settings),
    {
      onError: (error) => {
        console.error("Mutation failed:", error);
      },
      onSuccess: async() => {
        console.log("Mutation succeeded! Fetching new data...");
        const newData = await queryClient.fetchQuery(["settings"], fetchSettings);
        setTheme(newData?.theme);
      },
    }
  );
  return { data, mutate };
};


const updateAppView = async (updatedSettings: UserAppView) => {
  const res = await bffInstance.put<UserAppView>(
    "/updateUserAppView",
    updatedSettings
  );
  return res.data;
};

export const useUpdateAppView = ({ onSuccess}: any) => {
  const queryClient = useQueryClient();
  const { setAppView } = useUnifyUserProfileStore();
  const setIsMutating = useGlobalStore((state:any) => state.setIsMutating);

  const { data, mutate, isLoading } = useMutation(
    (settings: UserAppView) => updateAppView(settings),
    {
      onError: (error) => {
        console.error("Mutation failed:", error);
      },
      onSuccess: async() => {
        const newData = await queryClient.fetchQuery(["settings"], fetchSettings);
        setAppView(newData?.appView || 'default');
        onSuccess(newData?.appView);
      },
      onMutate: () => setIsMutating(true),
      onSettled: () => setIsMutating(false),
    }
  );
  return { data, mutate ,isLoading};
};

export const updateUserLocaleSettings = async (updatedLocale: UserLocale) => {
  const res = await bffInstance.patch<UserLocale>("/updateUserLocale", {
    userProfileUpdate: updatedLocale,
  });
  return res.data;
};

export function useUpdateUserLocaleSettings() {
  const queryClient = useQueryClient();
  const { setLocale } = useUnifyUserProfileStore();
  const setIsMutating = useGlobalStore((state:any) => state.setIsMutating);
  const setInsights = useNotificationStore((state) => state.setInsights);
  const { data, mutate } = useMutation(
    (locale: UserLocale) => updateUserLocaleSettings(locale),
    {
      onSuccess: (locale: UserLocale) => {
        setLocale(locale.locale);
        setInsights('', '');
        queryClient.invalidateQueries(["getCampaignsForHome"]);
      },
      onMutate: () => setIsMutating(true),
      onSettled: () => setIsMutating(false),
    }
  );
  return { data, mutate };
}


export function useUserSettings() {
  const { setTheme , setAppView } = useUnifyUserProfileStore();
  const { data = null, status } = useQuery({
    queryKey: ["settings"],
    queryFn: fetchSettings,
    onSuccess(data) {
      setTheme(data.theme);
      setAppView(data.appView || 'default')
    },
  });
  return { data, status };
}
